
import React, { useState, useEffect, useRef } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import axios from 'axios';

const BookingModal = ({ onClose }) => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [hostId, setHostId] = useState(null);
    const [bookingStatus, setBookingStatus] = useState(null);
    const [slotsLoading, setSlotsLoading] = useState(true);
    const [slotsError, setSlotsError] = useState(false);
    const formRef = useRef(null);
    const modalRef = useRef(null);

    useEffect(() => {
        let active = true;
        setSelectedSlot(null);
        setAvailableSlots([]);
        setSlotsLoading(true);
        setSlotsError(false);
        const fetchHostAndSlots = async () => {
            try {
                const hostResponse = await axios.get('https://us-central1-casey-portfolio-ai-1992.cloudfunctions.net/booking/v1/users/by-email', {
                    params: {
                        email: 'casey.shore.92@gmail.com',
                    },
                });

                const hostId = hostResponse.data.id;
                if (active) setHostId(hostId);

                const slotsResponse = await axios.get(`https://us-central1-casey-portfolio-ai-1992.cloudfunctions.net/booking/v1/booking/slots?host_id=${hostId}&start=${selectedDate.toISOString()}&end=${new Date(selectedDate.getTime() + 24 * 60 * 60 * 1000).toISOString()}`);
                if (active) setAvailableSlots(slotsResponse.data.slots);
            } catch (error) {
                if (active) setSlotsError(true);
            } finally {
                if (active) setSlotsLoading(false);
            }
        };

        fetchHostAndSlots();
        return () => { active = false; };
    }, [selectedDate]);

    useEffect(() => {
        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        modalRef.current?.focus();
        const handleKey = event => {
            if (event.key === 'Escape') { event.stopPropagation(); onClose(); }
            if (event.key === 'Tab') {
                const elements = [...modalRef.current.querySelectorAll('button:not(:disabled), a[href], input, textarea, select, [tabindex="0"]')].filter(el => el.getClientRects().length);
                const first = elements[0];
                const last = elements[elements.length - 1];
                if (event.shiftKey && (document.activeElement === first || document.activeElement === modalRef.current)) { event.preventDefault(); last?.focus(); }
                else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
            }
        };
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKey);
            document.body.style.overflow = previousOverflow;
            previousFocus?.focus();
        };
    }, [onClose]);

    const handleSlotSelection = (slot) => {
        setSelectedSlot(slot);
        setTimeout(() => {
            formRef.current?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }, 100);
    };

    const handleBooking = async (e) => {
        e.preventDefault();
        setBookingStatus('booking');

        const guestName = e.target.name.value;
        const guestEmail = e.target.email.value;
        const notes = e.target.notes.value;

        try {
            await axios.post('https://us-central1-casey-portfolio-ai-1992.cloudfunctions.net/booking/v1/booking', {
                host_id: hostId,
                start: selectedSlot.start,
                end: selectedSlot.end,
                guest_name: guestName,
                guest_email: guestEmail,
                notes: notes,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
            setBookingStatus('success');
        } catch (error) {
            setBookingStatus('error');
            console.error('Error booking meeting:', error);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex justify-center overflow-y-auto overscroll-contain bg-space-900/80 px-4 py-12 backdrop-blur-sm">
            <div ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="booking-heading" tabIndex="-1" className="card h-fit w-full max-w-4xl bg-space-700 p-6 sm:p-8">
                <div className="mb-6 flex items-center justify-between">
                    <h2 id="booking-heading" className="font-display text-2xl font-bold text-chalk">Schedule a meeting</h2>
                    <button onClick={onClose} aria-label="Close" className="text-chalk-mute transition-colors hover:text-chalk">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </button>
                </div>

                {bookingStatus === 'success' ? (
                    <div className="py-8 text-center" role="status">
                        <h3 className="font-display text-2xl font-bold text-emerald-300">Meeting booked</h3>
                        <p className="mt-3 text-chalk-dim">You will receive a confirmation email shortly.</p>
                    </div>
                ) : (
                    <form onSubmit={handleBooking}>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div>
                                <h3 className="mb-4 font-display text-lg font-semibold text-chalk">Select a date</h3>
                                <Calendar
                                    minDate={new Date()}
                                    onChange={setSelectedDate}
                                    value={selectedDate}
                                />
                            </div>
                            <div>
                                <h3 className="mb-4 font-display text-lg font-semibold text-chalk">Available time slots</h3>
                                <div className="mb-6 grid grid-cols-3 gap-3">
                                    {availableSlots.map(slot => (
                                        <button
                                            key={slot.start}
                                            type="button"
                                            className={`cursor-pointer rounded-lg border p-3 text-center text-sm transition-colors ${selectedSlot?.start === slot.start ? 'border-accent bg-accent text-white' : 'border-white/10 bg-white/[0.03] text-chalk-dim hover:border-white/25'}`}
                                            onClick={() => handleSlotSelection(slot)}
                                        >
                                            {new Date(slot.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </button>
                                    ))}
                                </div>

                                {slotsLoading && <p role="status" className="text-sm text-chalk-dim">Loading available times…</p>}
                                {slotsError && <p role="alert" className="text-sm text-chalk-dim">Available times could not be loaded. Try another date or <a className="text-link" href="mailto:casey.shore.92@gmail.com">email me to arrange a call</a>.</p>}
                                {!slotsLoading && !slotsError && availableSlots.length === 0 && (
                                    <p className="mb-6 text-sm text-chalk-mute">
                                        No open slots on this day. try another date.
                                    </p>
                                )}

                                {selectedSlot && (
                                    <div ref={formRef}>
                                        <div className="mb-4">
                                            <label htmlFor="name" className="mb-2 block text-sm font-semibold text-chalk">Full Name</label>
                                            <input type="text" id="name" name="name" required className="form-field w-full px-4 py-3" />
                                        </div>
                                        <div className="mb-4">
                                            <label htmlFor="email" className="mb-2 block text-sm font-semibold text-chalk">Email Address</label>
                                            <input type="email" id="email" name="email" required className="form-field w-full px-4 py-3" />
                                        </div>
                                        <div className="mb-4">
                                            <label htmlFor="notes" className="mb-2 block text-sm font-semibold text-chalk">Notes</label>
                                            <textarea id="notes" name="notes" rows="3" className="form-field w-full resize-none px-4 py-3"></textarea>
                                        </div>
                                        <button type="submit" className="btn-primary w-full rounded-xl px-8 py-4 font-semibold" disabled={bookingStatus === 'booking'}>
                                            {bookingStatus === 'booking' ? 'Booking...' : 'Book Meeting'}
                                        </button>
                                        {bookingStatus === 'error' && <p className="mt-3 text-sm text-red-300">Something went wrong. Please try again.</p>}
                                    </div>
                                )}
                            </div>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default BookingModal;
